<template>
  <div class="profile-menu" v-click-outside="closeMenuHandler">
    <div class="profile-menu__identity">
      <div class="profile-menu__identity-email">{{ user.email }}</div>
      <b-badge variant="outline-tonal" class="badge-xs">
        {{ getRoleName(role) }}
      </b-badge>
    </div>

    <!-- Correct handler -->
    <BButton
      variant="link"
      @click="toggleDropdown()"
      id="profile-dropdown-btn"
      class="me-0 profile-menu__button"
      :class="{ 'is-mobile-control': isMobileLayout }"
    >
      <div class="profile-menu__button-content">
        <svg-icon
          :icon="!isFakeLogin ? 'hp-user' : 'hp-eye'"
          class="profile-menu__icon icon-lg icon-color-normal"
          :class="{
            'text-success': isFakeLogin,
          }"
        />
      </div>
    </BButton>

    <MobileSlidingMenu
      v-if="isMobileLayout"
      :is-open="isOpen"
      side="right"
      menu-id="TheProfileMenu"
      @close="closeMenuHandler"
      width="90%"
      max-width="320px"
      class="side-menu"
    >
      <ProfileMenuContent
        :user="user"
        :full-name="fullName"
        :role="role"
        :is-admin="isAdmin"
        :can-impersonate="canImpersonate"
        :is-fake-login="isFakeLogin"
        :return-profile="returnProfile"
        :impersonation-profiles="impersonationProfiles"
        :filtered-profiles="filteredProfiles"
        :prepared-available-roles="preparedAvailableRoles"
        @open-fake-login-form="openFakeLoginForm"
        @switch-user="switchUser"
        @return-profile="returnProfileUser"
        @impersonate-profile="impersonateProfile"
        @impersonate-profile-new-tab="impersonateProfileNewTab"
        @remove-impersonation-profile="removeImpersonationProfile"
        @switch-role="switchRole"
        @close-menu="closeMenuHandler"
        @sign-out="signOut"
        @open-new-interface="openNewInterface"
        @exit-impersonation="exitImpersonation"
      />
    </MobileSlidingMenu>

    <!-- Desktop Version (Original Dropdown) -->
    <!-- Use ProfileMenuContent for desktop -->
    <div v-else-if="isOpen" class="profile-menu__dropdown dropdown-menu header-dropdown">
      <ProfileMenuContent
        :user="user"
        :full-name="fullName"
        :role="role"
        :is-admin="isAdmin"
        :can-impersonate="canImpersonate"
        :is-fake-login="isFakeLogin"
        :return-profile="returnProfile"
        :impersonation-profiles="impersonationProfiles"
        :filtered-profiles="filteredProfiles"
        :prepared-available-roles="preparedAvailableRoles"
        @open-fake-login-form="openFakeLoginForm"
        @switch-user="switchUser"
        @return-profile="returnProfileUser"
        @impersonate-profile="impersonateProfile"
        @impersonate-profile-new-tab="impersonateProfileNewTab"
        @remove-impersonation-profile="removeImpersonationProfile"
        @switch-role="switchRole"
        @close-menu="closeMenuHandler"
        @sign-out="signOut"
        @open-new-interface="openNewInterface"
        @exit-impersonation="exitImpersonation"
      />
    </div>

    <fake-login-form v-if="canImpersonate" ref="fake-login-form" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import { openNewInterface } from '@/auth/openNewInterface';
import { clearImpTokens, isImpersonating } from '@/auth/tabStorage';
import FakeLoginForm from '@/components/FakeLoginForm.vue';
import * as auth from '@/utils/auth';
import { useEventBus } from '@/utils/bus';
import { buildRoleSwitchHref, pickPreferredRole } from '@/utils/role';

import MobileSlidingMenu from '../Layout/MobileSlidingMenu.vue'; // Corrected path
import ProfileMenuContent from './ProfileMenuContent.vue'; // Corrected path relative to current dir

const IMPERSONATION_PROFILES_LIMIT = 5;

export default {
  components: {
    FakeLoginForm,
    MobileSlidingMenu, // Register the wrapper
    ProfileMenuContent, // Register the content component
  },

  data() {
    return {
      isOpen: false,
      savedImpersonationProfiles: [],
    };
  },

  created() {
    const { emit } = useEventBus();

    // Store emit reference for use in methods
    this.busEmit = emit;
    this.refreshSavedImpersonationProfiles();
  },

  computed: {
    ...mapState({
      userBalance: 'userBalance',
    }),
    ...mapGetters({
      availableRoles: 'availableRoles',
      role: 'role',
      user: 'user',
      fullName: 'fullName',
    }),
    isAdmin() {
      return ['advert-admin', 'manager'].includes(this.$store.getters.role);
    },
    canImpersonate() {
      const available = this.$store.getters.availableRoles ?? [];
      return !this.isFakeLogin && ['advert-admin', 'admin', 'manager'].some((r) => available.includes(r));
    },

    isFakeLogin() {
      // Primary: check sessionStorage-based impersonation (per-tab).
      if (isImpersonating()) {
        return true;
      }
      // Fallback: legacy localStorage marker for backward compat.
      const fakeLogin = auth.readFakeLogin();
      return fakeLogin.user ? fakeLogin.user === this.user.email : false;
    },
    returnProfile() {
      const fakeLogin = auth.readFakeLogin();
      if (!this.isFakeLogin || !fakeLogin.admin) {
        return null;
      }
      if (!this.profiles[fakeLogin.admin]) {
        return null;
      }
      return {
        email: fakeLogin.admin,
        token: this.profiles[fakeLogin.admin],
      };
    },
    impersonationProfiles() {
      const fakeLogin = auth.readFakeLogin();
      // The stored list is oldest first; the menu shows only the latest few, newest on top.
      return this.savedImpersonationProfiles
        .filter((profile) => profile.email !== this.user.email && profile.email !== fakeLogin.admin)
        .slice(-IMPERSONATION_PROFILES_LIMIT)
        .reverse();
    },

    preparedAvailableRoles() {
      return (
        this.availableRoles &&
        this.availableRoles
          .filter((el) => {
            return el !== this.role && el !== false;
          })
          .map((role) => {
            return {
              value: role,
              title: role.replace('-', ' ').replace(/\b[a-z](?=[a-z]{2})/g, (letter) => letter.toUpperCase()),
              href: buildRoleSwitchHref(role),
            };
          })
      );
    },
    isMobileLayout() {
      return this.$screenState.md || this.$screenState.sm || this.$screenState.xs;
    },
    filteredProfiles() {
      const profiles = [];
      const fakeLogins = auth.readFakeLoginsList();
      Object.keys(this.profiles).forEach((key) => {
        if (this.returnProfile?.email === key) {
          return;
        }
        if (this.user.email !== key && !fakeLogins.includes(key)) {
          profiles.push({
            email: key,
            token: this.profiles[key],
          });
        }
      });

      return profiles;
    },
    profiles() {
      return auth.readTokens();
    },
    refreshTokens() {
      return auth.readRefreshTokens();
    },
  },

  // mounted and beforeUnmount are no longer needed for listeners

  watch: {
    // Watcher for overlay is simplified - only needed for desktop now
    isOpen(val) {
      if (!this.isMobileLayout && !val) {
        this.busEmit('overlay-off');
      }
      // Mobile overlay handled by toggleDropdown
    },
    // Close on route change (applies to both mobile and desktop here)
    $route() {
      this.closeMenuHandler();
    },
    // Add/remove listeners if layout changes while component is mounted
    isMobileLayout(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }
      // Ensure menu is closed when switching layouts
      this.closeMenuHandler();
    },
  },

  methods: {
    // Removed add/removeMobileListeners and handleCloseOthers
    ...mapActions({
      fetchTargets: 'advert/fetchTargets',
    }),
    refreshSavedImpersonationProfiles() {
      const ids = auth.readFakeLoginIds();
      this.savedImpersonationProfiles = auth
        .readFakeLoginsList()
        .filter((email) => ids[email])
        .map((email) => ({ email }));
    },
    getRoleName(input) {
      if (!input) {
        return '';
      }
      let result = '';
      const words = input.split('-');
      words.forEach((word, index) => {
        if (index === 0) {
          result += word;
        } else {
          result += word.charAt(0).toUpperCase() + word.slice(1);
        }
      });

      return this.$t(result);
    },
    switchRole(role) {
      this.busEmit('switch-role:start');
      this.closeMenuHandler(); // Close menu before switching
      this.$store.dispatch('switchRole', role).then(
        (res) => {
          this.busEmit('switch-role:end');
          if (!this.$route.meta.roles) {
            return res;
          }
          this.$router.push({
            name: this.getRouteName(),
          });
          this.fetchTargets({ refresh: true });
          return res;
        },
        (e) => e,
      );
    },
    switchUser(email) {
      auth.saveUser({ email });
      this.closeMenuHandler(); // Close menu before switching
      this.$store.dispatch('fetchUser').then(
        (res) => {
          if (res) {
            const available = this.$store.getters.availableRoles ?? [];
            const preferred = pickPreferredRole(available) ?? available[0];
            if (preferred) {
              this.switchRole(preferred);
            }
            this.$router.go();
          }
        },
        (e) => e,
      );
      this.$api.updateCredentials({
        email: email,
        token: this.profiles[email],
        refreshToken: this.refreshTokens[email],
      });
    },
    getReturnRole() {
      const fakeLogin = auth.readFakeLogin();
      const privilegedRoles = ['advert-admin', 'admin', 'manager'];
      return privilegedRoles.includes(fakeLogin.adminRole) ? fakeLogin.adminRole : 'admin';
    },
    returnProfileUser(email, { removeCurrentImpersonation = false } = {}) {
      const currentImpersonationEmail = this.user.email;
      auth.saveUser({ email });
      clearImpTokens();
      auth.saveActiveRole(this.getReturnRole());
      sessionStorage.setItem('role', this.getReturnRole());
      auth.saveFakeLogin();
      if (removeCurrentImpersonation) {
        auth.deleteFakeLoginEntry(currentImpersonationEmail);
      }
      this.closeMenuHandler();
      window.location.reload();
    },
    impersonateProfile(email) {
      this.closeMenuHandler();
      this.$refs['fake-login-form'].loginAsEmail(email);
    },
    impersonateProfileNewTab(email) {
      this.closeMenuHandler();
      this.$refs['fake-login-form'].loginAsEmail(email, { inNewTab: true });
    },
    removeImpersonationProfile(email) {
      auth.deleteFakeLoginEntry(email);
      this.savedImpersonationProfiles = this.savedImpersonationProfiles.filter((profile) => profile.email !== email);
    },

    async openFakeLoginForm() {
      this.refreshSavedImpersonationProfiles();
      this.$refs['fake-login-form'].openForm();
    },
    toggleDropdown() {
      const opening = !this.isOpen;
      this.isOpen = opening;
      // Emit generic close event if opening on mobile
      if (this.isMobileLayout && opening) {
        this.busEmit('mobile-menu:close-others', 'TheProfileMenu');
        // Handle overlay directly for mobile open
        this.busEmit('overlay-on');
      } else if (!this.isMobileLayout && !opening) {
        // Handle overlay directly for desktop close
        this.busEmit('overlay-off');
      } else if (this.isMobileLayout && !opening) {
        // Handle overlay directly for mobile close
        this.busEmit('overlay-off');
      }
    },
    closeMenuHandler() {
      // Renamed from hideHandler for clarity
      if (!this.isOpen) {
        return;
      }
      const wasOpen = this.isOpen;
      this.isOpen = false;
      // Handle overlay directly for mobile close
      if (wasOpen && this.isMobileLayout) {
        this.busEmit('overlay-off');
      }
    },
    exitImpersonation() {
      const fakeLogin = auth.readFakeLogin();
      const returnEmail = fakeLogin.admin || auth.readUser();
      this.returnProfileUser(returnEmail, { removeCurrentImpersonation: true });
    },
    openNewInterface() {
      this.closeMenuHandler();
      openNewInterface(this.$route.path);
    },
    signOut() {
      this.closeMenuHandler(); // Close menu before signing out
      this.$store.dispatch('logout');
    },
    getRouteName() {
      switch (auth.readActiveRole()) {
        case 'admin':
          return 'users';
        case 'ad-exchange':
          return 'stats';
        case 'advertiser':
          return 'stats';
        case 'advert-admin':
        case 'manager':
        default:
          return 'dashboard';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-menu {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;

  @media (max-width: $hp-breakpoint-mobile-menu) {
    margin-right: 0;
    height: 100%;
    border-left: 1px solid var(--bs-border-color);
    width: var(--hp-header-mobile-height);
  }
}

.profile-menu__identity {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-right: 0.75rem;

  @media (max-width: $hp-breakpoint-mobile-menu) {
    display: none;
  }
}

.profile-menu__identity-email {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: right;
  margin: 0;
  font-size: 0.75rem;
  font-family: var(--font-roboto);
  color: var(--bs-body-color-muted);
  position: relative;
  bottom: 2px;
}

.profile-menu__button {
  height: 2.5rem;
  width: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bs-border-color);
  border-radius: 50%;
  border: none;
  color: var(--bs-body-color);
  font-size: var(--bs-body-font-size-sm);
  cursor: pointer;
  transition: all 0.2s ease;

  @media (max-width: $hp-breakpoint-mobile-menu) {
    background: transparent;
    border-radius: 0;
    height: 100%;
    width: 100%;
    padding: 0;
    margin: 0;
  }

  &:hover:not(:disabled) {
    border: 1px solid var(--bs-border-color);
    background: transparent;

    @media (max-width: $hp-breakpoint-mobile-menu) {
      border: none;
    }
  }

  &:active,
  &:focus {
    border-color: var(--bs-form-element-focus-border-color);
    box-shadow: var(--bs-form-element-focus-box-shadow);
    text-decoration: none;

    @media (max-width: $hp-breakpoint-mobile-menu) {
      border: none;
      box-shadow: none;
    }
  }
}

.profile-menu__avatar {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.profile-menu__details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.profile-menu__dropdown.header-dropdown {
  min-width: 320px;
  margin-top: calc(var(--bs-spacer) * 0.5);
  right: 0;
  left: auto;
  display: block;
  z-index: 1025;
  position: absolute;
  font-family: 'Onest', sans-serif;
  height: auto;
  top: 35px;
  transition: opacity 0.2s;
  opacity: 1;
  border: var(--bs-border-width) solid var(--bs-border-color);
  box-shadow: var(--bs-box-shadow);
}

.profile-menu__dropdown-content {
  height: auto;
}

.profile-menu__button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  @media (max-width: $hp-breakpoint-mobile-menu) {
    width: 100%;
    height: 100%;
  }
}
</style>
